import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal
} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {
  NgbDropdown,
  NgbDropdownItem,
  NgbDropdownMenu,
  NgbDropdownToggle,
  NgbPagination
} from '@ng-bootstrap/ng-bootstrap';
import {firstValueFrom} from 'rxjs';
import {
  BusinessStockDto,
  SaleOfferDto,
  TradeRecordDto,
  TradeRecordKind
} from '../../../../models/generated-models';
import {ConfirmDialogService} from '../../../shared/commons/dialog/confirm-dialog/confirm-dialog.service';
import {FruitIconComponent} from '../../../shared/commons/fruit-icon/fruit-icon.component';
import {PageTitleComponent} from '../../../shared/commons/page-title/page-title.component';
import {ThaiDateTimePipe} from '../../../shared/commons/thai-datetime.pipe';
import {QrCodeModalService} from '../../../shared/commons/trade/qr-code-modal.service';
import {RecordCodeComponent} from '../../../shared/commons/trade/record-code.component';
import {TradeApiService} from '../trade-api.service';
import {TradeStore} from '../trade.store';
import {AddBusinessFlowService} from '../../user-business/add/add-business-flow.service';
import {TradeBusinessSelectComponent} from '../trade-business-select.component';
import {
  DEFAULT_TRADE_UNIT,
  TRADE_BASE,
  saleOfferStatusBadgeClass,
  saleOfferStatusLabel,
  tradeKindBadgeClass,
  tradeKindLabel,
  tradeRecordLabel
} from '../trade.constants';
import {formatQuantity, formatQuantityWithUnit, offerComponentsBreakdown} from '../trade-utils';

/** Top-level sections of the trade page, shown as tabs. */
type MainTab = 'RECORDS' | 'STOCK' | 'OFFERS';

/** "ทั้งหมด" / "ที่ขายแล้ว" — the two status tabs of the records table. */
type RecordTab = 'ALL' | 'SOLD';

/** How long a just-added stock card stays flagged, and how long its number counts up. */
const ADDED_STOCK_FLAG_MS = 8000;
const ADDED_STOCK_COUNT_UP_MS = 1400;

/** A record counts as sold once any quantity has left it. */
function isRecordSold(record: TradeRecordDto): boolean {
  return Number(record.soldQty ?? 0) > 0;
}

/**
 * What the status pill says about a lot: how much of it has left (sold on, consumed by a
 * transform, or exported). Records have no workflow of their own — this is read off the
 * quantities, and the wording is the lot's, not the sale offer's.
 */
type LotStockStatus = 'UNSOLD' | 'PARTIAL' | 'SOLD_OUT' | 'EXPORTED';

function lotStockStatus(record: TradeRecordDto): LotStockStatus {
  if (record.export) {
    return 'EXPORTED';
  }
  const sold = Number(record.soldQty ?? 0);
  const remaining = Number(record.remainingQty ?? 0);
  if (sold <= 0) {
    return 'UNSOLD';
  }
  return remaining > 0 ? 'PARTIAL' : 'SOLD_OUT';
}

const LOT_STOCK_STATUS_LABELS: Record<LotStockStatus, string> = {
  UNSOLD: 'ยังไม่ได้ขาย',
  PARTIAL: 'ขายบางส่วน',
  SOLD_OUT: 'ขายหมดแล้ว',
  EXPORTED: 'ส่งออกแล้ว'
};

const LOT_STOCK_STATUS_CLASSES: Record<LotStockStatus, string> = {
  UNSOLD: 'st-unsold',
  PARTIAL: 'st-partial',
  SOLD_OUT: 'st-closed',
  EXPORTED: 'st-closed'
};

@Component({
  selector: 'app-trade-list',
  standalone: true,
  imports: [
    FormsModule,
    RouterLink,
    NgbDropdown,
    NgbDropdownToggle,
    NgbDropdownMenu,
    NgbDropdownItem,
      PageTitleComponent,
    ThaiDateTimePipe,
    FruitIconComponent,
    RecordCodeComponent,
    TradeBusinessSelectComponent
  ],
  templateUrl: './trade-list.component.html',
  styleUrl: './trade-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TradeListComponent implements OnInit {
  private readonly api = inject(TradeApiService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly qrModal = inject(QrCodeModalService);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly store = inject(TradeStore);
  private readonly addBusinessFlow = inject(AddBusinessFlowService);

  protected readonly records = signal<TradeRecordDto[]>([]);
  protected readonly stock = signal<BusinessStockDto[]>([]);
  protected readonly offers = signal<SaleOfferDto[]>([]);
  protected readonly recordsLoading = signal(false);
  /** What the box shows; `search` is the debounced value the query uses. */
  protected readonly searchInput = signal('');
  protected readonly search = signal('');
  protected readonly selectedProduct = signal('');
  protected readonly kindFilter = signal<TradeRecordKind | ''>('');
  protected readonly activeTab = signal<RecordTab>('ALL');
  protected readonly activeMainTab = signal<MainTab>('RECORDS');
  protected readonly manualCode = signal('');
  /** Bumped after a write so every list on the page refetches (zoneless). */
  private readonly reloadToken = signal(0);

  /** Initial visible count per section and step size when clicking "เพิ่มเติม" */
  protected readonly initialLimit = 5;
  protected readonly limitStep = 5;

  protected readonly stockLimit = signal(5);
  protected readonly offerLimit = signal(5);
  protected readonly recordLimit = signal(5);

  protected readonly kindOptions: Array<{value: TradeRecordKind | ''; label: string}> = [
    {value: '', label: 'ทุกประเภท'},
    {value: 'HARVEST', label: tradeKindLabel('HARVEST')},
    {value: 'PURCHASE', label: tradeKindLabel('PURCHASE')},
    {value: 'SHIPMENT', label: tradeKindLabel('SHIPMENT')},
    {value: 'TRANSFORM', label: tradeKindLabel('TRANSFORM')}
  ];

  protected readonly statusTabs: Array<{value: RecordTab; label: string}> = [
    {value: 'ALL', label: 'ทั้งหมด'},
    {value: 'SOLD', label: 'จำหน่ายแล้ว'}
  ];

  protected readonly mainTabs: Array<{value: MainTab; label: string; icon: string; imageIcon: string}> = [
    {value: 'RECORDS', label: 'รายการซื้อ-ขาย', icon: 'bi-basket2', imageIcon: 'assets/images/icons/product.png'},
    {value: 'STOCK', label: 'สต็อกสินค้าคงเหลือ', icon: 'bi-box-seam', imageIcon: 'assets/images/icons/cardboard.png'},
    {value: 'OFFERS', label: 'ใบขาย', icon: 'bi-receipt', imageIcon: 'assets/images/icons/shopping-cart.png'}
  ];

  protected readonly unit = DEFAULT_TRADE_UNIT;
  protected readonly tradeBase = TRADE_BASE;
  protected readonly tradeKindLabel = tradeKindLabel;
  protected readonly tradeRecordLabel = tradeRecordLabel;
  protected readonly tradeKindBadgeClass = tradeKindBadgeClass;
  protected readonly saleOfferStatusLabel = saleOfferStatusLabel;
  protected readonly saleOfferStatusBadgeClass = saleOfferStatusBadgeClass;
  protected readonly formatQuantity = formatQuantity;
  protected readonly formatQuantityWithUnit = formatQuantityWithUnit;

  protected shortUnit(unit: string | null | undefined): string {
    if (!unit) {
      return '';
    }
    const trimmed = unit.trim();
    return trimmed === 'กิโลกรัม' ? 'กก.' : trimmed;
  }

  /**
   * Pools a form just added to (transform output, received lines) with the amount added,
   * handed over in the navigation state. Their cards pop, count up from the previous amount
   * and float a "+qty" chip for a few seconds, so the change is visible without opening the
   * record — the coupon-collected feel, for stock.
   */
  protected readonly addedStock = signal<ReadonlyMap<string, number>>(new Map());
  /** 0 → 1 over {@link ADDED_STOCK_COUNT_UP_MS}: how far the count-up has run. */
  protected readonly countUpProgress = signal(1);
  private addedStockTimer: ReturnType<typeof setTimeout> | null = null;
  private countUpFrame: number | null = null;

  protected readonly hasNoBusiness = computed(
    () => this.store.businessesLoaded() && this.store.businesses().length === 0
  );

  /** Selling and processing act on one business's pool, so one must be picked. */
  protected readonly canSell = computed(() => this.store.selectedBusinessId() != null);

  /**
   * Several businesses and none picked yet (nothing remembered for this account): the page
   * asks for one instead of pouring every business's stock and records into one list.
   */
  protected readonly needsBusinessChoice = computed(
    () => this.store.businessesLoaded() && this.store.businesses().length > 1
      && this.store.selectedBusinessId() == null
  );

  /** Product options for the filter come from the loaded rows, so they always match the view. */
  protected readonly productOptions = computed(() => {
    const names = new Set<string>();
    for (const record of this.records()) {
      const name = (record.productName ?? '').trim();
      if (name) {
        names.add(name);
      }
    }
    return [...names].sort((a, b) => a.localeCompare(b, 'th'));
  });

  /**
   * The backend already filters by `search`, but the box narrows the rows on
   * every keystroke so the table reacts before the debounced request lands.
   */
  protected readonly visibleRecords = computed(() => {
    const term = this.searchInput().trim().toLowerCase();
    const product = this.selectedProduct();
    return this.records().filter((record) => {
      if (product && (record.productName ?? '') !== product) {
        return false;
      }
      if (!term) {
        return true;
      }
      return [
        record.productName,
        record.businessTitle,
        record.sellerBusinessTitle,
        record.recordCode,
        record.lotCode,
        record.farmName
      ].some((value) => (value ?? '').toLowerCase().includes(term));
    });
  });

  /** The count shown on the "ที่ขายแล้ว" tab follows the search, like the old page. */
  protected readonly soldCount = computed(() => this.visibleRecords().filter(isRecordSold).length);

  protected readonly filteredRecords = computed(() =>
    this.activeTab() === 'SOLD' ? this.visibleRecords().filter(isRecordSold) : this.visibleRecords()
  );

  protected readonly pagedRecords = computed(() =>
    this.filteredRecords().slice(0, this.recordLimit())
  );

  protected readonly pagedStock = computed(() =>
    this.stock().slice(0, this.stockLimit())
  );

  protected readonly pagedOffers = computed(() =>
    this.offers().slice(0, this.offerLimit())
  );

  private searchTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // Zoneless: every reload is driven by the signal reads inside these effects.
    effect(() => {
      const businessId = this.store.selectedBusinessId();
      const kind = this.kindFilter();
      const search = this.search();
      this.reloadToken();
      if (!this.store.businessesLoaded()) {
        return;
      }
      if (businessId == null) {
        // Never the "every business" view — a business is auto-picked or asked for.
        this.records.set([]);
        return;
      }
      this.loadRecords(businessId, kind, search);
    });

    // The stock and the offers only depend on the business, so typing does not refetch them.
    effect(() => {
      const businessId = this.store.selectedBusinessId();
      this.reloadToken();
      if (!this.store.businessesLoaded()) {
        return;
      }
      if (businessId == null) {
        this.stock.set([]);
        this.offers.set([]);
        return;
      }
      this.loadStock(businessId);
      this.loadOffers(businessId);
    });

    this.destroyRef.onDestroy(() => {
      if (this.searchTimer != null) {
        clearTimeout(this.searchTimer);
      }
      if (this.addedStockTimer != null) {
        clearTimeout(this.addedStockTimer);
      }
      if (this.countUpFrame != null) {
        cancelAnimationFrame(this.countUpFrame);
      }
    });
  }

  ngOnInit(): void {
    const tab = this.route.snapshot.queryParamMap?.get('tab')?.toUpperCase();
    if (tab === 'STOCK' || tab === 'OFFERS' || tab === 'RECORDS') {
      this.activeMainTab.set(tab as MainTab);
    }
    this.flagAddedStock();
    void this.store.loadBusinesses();
  }

  protected isAddedStock(item: BusinessStockDto): boolean {
    const name = item.productName?.trim();
    return !!name && this.addedStock().has(name);
  }

  /** What was just added to this pool, for the floating "+qty" chip. */
  protected addedQuantity(item: BusinessStockDto): number {
    return this.addedStock().get(item.productName?.trim() ?? '') ?? 0;
  }

  /** The number on the card: counts up from the amount before the addition to the new total. */
  protected displayQuantity(item: BusinessStockDto): number {
    const total = Number(item.quantity ?? 0);
    const added = this.addedQuantity(item);
    if (added <= 0) {
      return total;
    }
    const eased = 1 - Math.pow(1 - this.countUpProgress(), 3);
    return Math.max(0, total - added * (1 - eased));
  }

  private flagAddedStock(): void {
    // Angular 21: lastSuccessfulNavigation is a signal; history.state covers a plain reload.
    const state = (this.router.lastSuccessfulNavigation()?.extras?.state ?? history.state) as
      {addedStock?: unknown} | null;
    const added = new Map<string, number>();
    for (const line of Array.isArray(state?.addedStock) ? state.addedStock : []) {
      const name = typeof line?.productName === 'string' ? line.productName.trim() : '';
      const quantity = Number(line?.quantity ?? 0);
      if (name !== '') {
        added.set(name, (added.get(name) ?? 0) + (Number.isFinite(quantity) && quantity > 0 ? quantity : 0));
      }
    }
    if (added.size === 0) {
      return;
    }
    this.addedStock.set(added);
    this.addedStockTimer = setTimeout(() => this.addedStock.set(new Map()), ADDED_STOCK_FLAG_MS);
  }

  /** Runs the count-up once the flagged cards are on screen. */
  private startCountUp(): void {
    if (this.addedStock().size === 0 || typeof requestAnimationFrame !== 'function') {
      return;
    }
    if (this.countUpFrame != null) {
      cancelAnimationFrame(this.countUpFrame);
    }
    this.countUpProgress.set(0);
    const started = performance.now();
    const step = (now: number): void => {
      const progress = Math.min(1, (now - started) / ADDED_STOCK_COUNT_UP_MS);
      this.countUpProgress.set(progress);
      this.countUpFrame = progress < 1 ? requestAnimationFrame(step) : null;
    };
    this.countUpFrame = requestAnimationFrame(step);
  }

  protected onBusinessChange(businessId: number | null): void {
    this.store.selectBusiness(businessId);
    this.stockLimit.set(this.initialLimit);
    this.offerLimit.set(this.initialLimit);
    this.recordLimit.set(this.initialLimit);
  }

  protected loadMoreStock(): void {
    this.stockLimit.update((limit) => limit + this.limitStep);
  }

  protected loadMoreOffers(): void {
    this.offerLimit.update((limit) => limit + this.limitStep);
  }

  protected loadMoreRecords(): void {
    this.recordLimit.update((limit) => limit + this.limitStep);
  }

  protected onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value ?? '';
    this.searchInput.set(value);
    this.recordLimit.set(this.initialLimit);
    if (this.searchTimer != null) {
      clearTimeout(this.searchTimer);
    }
    this.searchTimer = setTimeout(() => this.search.set(value), 300);
  }

  protected onKindChange(event: Event): void {
    this.kindFilter.set((event.target as HTMLSelectElement).value as TradeRecordKind | '');
    this.recordLimit.set(this.initialLimit);
  }

  protected onProductChange(event: Event): void {
    this.selectedProduct.set((event.target as HTMLSelectElement).value);
    this.recordLimit.set(this.initialLimit);
  }

  /**
   * Offer status badge class matching status text styles (.st-open, .st-closed, .st-cancelled)
   */
  protected saleOfferStatusClass(status: any): string {
    switch (status) {
      case 'OPEN':
        return 'st-open';
      case 'ACCEPTED':
        return 'st-closed';
      case 'CANCELLED':
        return 'st-cancelled';
      default:
        return 'st-open';
    }
  }

  /** The lot's stock status pill (ยังไม่ได้ขาย / ขายบางส่วน / ขายหมดแล้ว / ส่งออกแล้ว). */
  protected recordSaleStatusLabel(record: TradeRecordDto): string {
    return LOT_STOCK_STATUS_LABELS[lotStockStatus(record)];
  }

  protected recordSaleStatusClass(record: TradeRecordDto): string {
    return LOT_STOCK_STATUS_CLASSES[lotStockStatus(record)];
  }

  /** A purchase really moved seller → own business; other kinds have no buyer on the record. */
  protected flowFrom(record: TradeRecordDto): string {
    if (record.kind === 'PURCHASE') {
      return record.sellerBusinessTitle?.trim() || '—';
    }
    return record.businessTitle?.trim() || '—';
  }

  protected flowTo(record: TradeRecordDto): string | null {
    if (record.kind !== 'PURCHASE') {
      return null;
    }
    const buyer = record.businessTitle?.trim() || '';
    return buyer && buyer !== this.flowFrom(record) ? buyer : null;
  }

  protected selectTab(tab: RecordTab): void {
    this.activeTab.set(tab);
    this.recordLimit.set(this.initialLimit);
  }

  protected recordRowNumber(index: number): number {
    return index + 1;
  }

  protected selectMainTab(tab: MainTab): void {
    this.activeMainTab.set(tab);
  }

  protected onManualCodeInput(event: Event): void {
    this.manualCode.set((event.target as HTMLInputElement).value ?? '');
  }

  protected counterparty(record: TradeRecordDto): string {
    return record.kind === 'HARVEST' ? '—' : record.sellerBusinessTitle?.trim() || '—';
  }

  /** A harvest is dated by the harvest itself; anything else by when it was recorded. */
  protected recordDate(record: TradeRecordDto): Date | string | null {
    return (record.harvestDate ?? record.recordedAt ?? null) as Date | string | null;
  }

  protected openRecord(record: TradeRecordDto): void {
    if (record.id != null) {
      void this.router.navigate([TRADE_BASE, record.id]);
    }
  }

  protected editRecord(record: TradeRecordDto, event?: Event): void {
    event?.stopPropagation();
    if (record.id != null) {
      void this.router.navigate([TRADE_BASE, record.id, 'edit']);
    }
  }

  protected deleteRecord(record: TradeRecordDto, event?: Event): void {
    event?.stopPropagation();
    const id = record.id;
    if (id == null) {
      return;
    }
    void firstValueFrom(this.confirmDialog.open({
      title: 'ยืนยันการลบ',
      content: 'ต้องการลบรายการนี้หรือไม่ ระบบจะคืนสต๊อกให้รายการต้นทาง',
      confirmButtonMsg: 'ลบรายการ',
      closeButtonMsg: 'ยกเลิก'
    })).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      this.api.deleteRecord(id).subscribe({
        next: () => this.reload(),
        error: () => undefined
      });
    }).catch(() => undefined);
  }

  /** The seller decides the quantity, so selling starts from the stock card. */
  protected openSell(item: BusinessStockDto): void {
    void this.router.navigate([TRADE_BASE, 'sell'], {
      queryParams: {productName: item.productName}
    });
  }

  /** Processing draws from the pooled stock line, so it starts from the card. */
  protected openTransform(item: BusinessStockDto): void {
    void this.router.navigate([TRADE_BASE, 'transform'], {
      queryParams: {productName: item.productName}
    });
  }

  protected openOffer(offer: SaleOfferDto): void {
    if (offer.id != null) {
      void this.router.navigate([TRADE_BASE, 'offers', offer.id]);
    }
  }

  /** "น้อยหน่า 600 กิโลกรัม + ทุเรียนอบแห้ง 50 กิโลกรัม" */
  protected offerSummary(offer: SaleOfferDto): string {
    const items = offer.items ?? [];
    if (items.length === 0) {
      return '-';
    }
    return items
      .map((item) => {
        const breakdown = offerComponentsBreakdown(item.components);
        const quantity = formatQuantityWithUnit(item.quantity, item.unit || this.unit);
        return `${item.productName ?? '-'} ${quantity}${breakdown ? ` ${breakdown}` : ''}`;
      })
      .join(' + ');
  }

  protected offerTotal(offer: SaleOfferDto): number {
    return offer.totalQuantity ?? (offer.items ?? []).reduce((sum, item) => sum + (item.quantity ?? 0), 0);
  }

  /** What the buyer actually took; the remainder of a partly taken offer went back to stock. */
  protected offerAcceptedTotal(offer: SaleOfferDto): number {
    return (offer.items ?? []).reduce((sum, item) => sum + (item.acceptedQuantity ?? item.quantity ?? 0), 0);
  }

  protected offerPartiallyAccepted(offer: SaleOfferDto): boolean {
    return offer.status === 'ACCEPTED'
      && (offer.items ?? []).some((item) => item.acceptedQuantity != null && item.acceptedQuantity !== item.quantity);
  }

  protected openOfferQr(offer: SaleOfferDto): void {
    void this.qrModal.open({
      title: offer.items?.[0]?.productName || 'QR ใบขาย',
      subtitle: 'ให้ผู้ซื้อสแกนเพื่อรับสินค้าตามจำนวนในใบขาย',
      code: offer.offerCode
    });
  }

  protected cancelOffer(offer: SaleOfferDto): void {
    const id = offer.id;
    if (id == null) {
      return;
    }
    void firstValueFrom(this.confirmDialog.open({
      title: 'ยืนยันการยกเลิก',
      content: 'ต้องการยกเลิกใบขายนี้หรือไม่ ระบบจะคืนสินค้าเข้าสต็อก',
      confirmButtonMsg: 'ยกเลิกใบขาย',
      closeButtonMsg: 'ปิด'
    })).then((confirmed) => {
      if (!confirmed) {
        return;
      }
      this.api.cancelOffer(id).subscribe({
        next: () => this.reload(),
        error: () => undefined
      });
    }).catch(() => undefined);
  }

  protected openScanner(): void {
    // The scanner navigates to `target?code=…`; it has to be the real receive route.
    void this.router.navigate(['/scan'], {
      queryParams: {returnTo: TRADE_BASE, target: `${TRADE_BASE}/receive`}
    });
  }

  protected submitManualCode(): void {
    const code = this.manualCode().trim();
    if (!code) {
      return;
    }
    void this.router.navigate([TRADE_BASE, 'receive'], {queryParams: {code}});
  }

  /** "รีเฟรช" — the records, the stock pools and the offers all come back fresh. */
  protected refresh(): void {
    this.reload();
  }

  private reload(): void {
    this.reloadToken.update((value) => value + 1);
  }

  private loadRecords(businessId: number | null, kind: TradeRecordKind | '', search: string): void {
    this.recordsLoading.set(true);
    this.api.listRecords({businessId, kind: kind || null, search}).subscribe({
      next: (items) => {
        this.records.set(items ?? []);
        this.recordsLoading.set(false);
      },
      error: () => {
        this.records.set([]);
        this.recordsLoading.set(false);
      }
    });
  }

  private loadStock(businessId: number | null): void {
    this.api.listStock(businessId).subscribe({
      // Empty pools stay in the database for history but have nothing to sell, process or show.
      next: (items) => {
        this.stock.set((items ?? []).filter((item) => Number(item.quantity ?? 0) > 0));
        this.startCountUp();
      },
      error: () => this.stock.set([])
    });
  }

  private loadOffers(businessId: number | null): void {
    this.api.listOffers({businessId}).subscribe({
      next: (items) => this.offers.set(items ?? []),
      error: () => this.offers.set([])
    });
  }

  /** Same flow as "+ เพิ่มธุรกิจ" on the business page: choose the form flavour, then the add form. */
  protected addBusiness(): void {
    this.addBusinessFlow.start();
  }
}
